import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertTriangle,
  CheckCircle2,
  ClipboardCheck,
  Copy,
  FileKey,
  LockKeyhole,
  PlugZap,
  RefreshCw,
  Settings,
  ShieldCheck,
  Terminal,
  X,
} from 'lucide-react';

export interface McpConnectionProfile {
  title: string;
  serviceLabel: string;
  serverName: string;
  url: string;
  environmentVariable: string;
  purpose: string;
}

interface McpConnectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  profile: McpConnectionProfile | null;
}

type TabId = 'overview' | 'config' | 'env' | 'validation' | 'security';

const tabs: { id: TabId; label: string; icon: React.ElementType }[] = [
  { id: 'overview', label: 'Vue d’ensemble', icon: PlugZap },
  { id: 'config', label: 'config.yaml', icon: Settings },
  { id: 'env', label: '.env', icon: FileKey },
  { id: 'validation', label: 'Validation', icon: ClipboardCheck },
  { id: 'security', label: 'Sécurité', icon: ShieldCheck },
];

function Panel({ title, icon: Icon, children, tone = 'cyan' }: {
  title: string;
  icon: React.ElementType;
  children: React.ReactNode;
  tone?: 'cyan' | 'amber' | 'emerald' | 'rose';
}) {
  const tones = {
    cyan: 'border-cyan-300/20 bg-cyan-400/5',
    amber: 'border-amber-300/25 bg-amber-400/10',
    emerald: 'border-emerald-300/20 bg-emerald-400/5',
    rose: 'border-rose-300/25 bg-rose-400/10',
  };

  return (
    <section className={`rounded-2xl border p-5 ${tones[tone]}`}>
      <h3 className="mb-4 flex items-center gap-2 text-lg font-black text-white">
        <Icon className="h-5 w-5 text-cyan-200" />
        {title}
      </h3>
      {children}
    </section>
  );
}

function CodeBlock({ children, label = 'Copier' }: { children: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(children);
      setCopyFailed(false);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopyFailed(true);
    }
  };

  return (
    <div className="group relative my-3 overflow-hidden rounded-xl border border-slate-700 bg-slate-950">
      <pre className="overflow-x-auto p-4 pr-12 text-xs leading-6 text-emerald-300"><code>{children}</code></pre>
      <button
        type="button"
        onClick={() => void copy()}
        className="absolute right-2 top-2 rounded-lg border border-slate-600 bg-slate-800 p-2 text-slate-300 hover:bg-slate-700"
        title={label}
        aria-label={label}
      >
        {copied ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}
      </button>
      <span className="sr-only" aria-live="polite">
        {copied ? 'Contenu copié.' : copyFailed ? 'La copie a échoué.' : ''}
      </span>
    </div>
  );
}

function Bullets({ items }: { items: React.ReactNode[] }) {
  return (
    <ul className="space-y-2 text-sm leading-6 text-slate-300">
      {items.map((item, index) => (
        <li key={index} className="flex gap-2">
          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-cyan-400" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function OverviewTab({ profile }: { profile: McpConnectionProfile }) {
  return (
    <div className="space-y-5">
      <Panel title="Objectif" icon={PlugZap}>
        <p className="text-sm leading-6 text-slate-300">{profile.purpose}</p>
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          {[
            ['Nom du serveur', profile.serverName],
            ['Transport', 'MCP HTTP distant'],
            ['Point de terminaison', profile.url],
            ['Authentification', `Bearer via ${profile.environmentVariable}`],
          ].map(([label, value]) => (
            <div key={label} className="rounded-xl border border-white/10 bg-black/20 p-4">
              <p className="text-xs font-bold uppercase tracking-wider text-cyan-300">{label}</p>
              <p className="mt-1 break-words text-sm font-semibold text-white">{value}</p>
            </div>
          ))}
        </div>
      </Panel>

      <Panel title="Principe de configuration" icon={LockKeyhole} tone="emerald">
        <p className="text-sm leading-6 text-slate-300">
          Le fichier <code className="text-emerald-300">config.yaml</code> référence uniquement la variable <code className="text-emerald-300">${`{${profile.environmentVariable}}`}</code>. La vraie clé demeure dans le fichier <code className="text-emerald-300">.env</code> du profil Hermes actif.
        </p>
      </Panel>
    </div>
  );
}

function ConfigTab({ profile }: { profile: McpConnectionProfile }) {
  const yamlConfig = `mcp_servers:\n  ${profile.serverName}:\n    url: ${profile.url}\n    headers:\n      Authorization: Bearer \${${profile.environmentVariable}}\n    enabled: true`;
  const commands = `hermes config set mcp_servers.${profile.serverName}.url ${profile.url}\nhermes config set mcp_servers.${profile.serverName}.headers.Authorization 'Bearer \${${profile.environmentVariable}}'\nhermes config set mcp_servers.${profile.serverName}.enabled true`;

  return (
    <div className="space-y-5">
      <Panel title="Ouvrir la configuration Hermes" icon={FileKey}>
        <p className="text-sm leading-6 text-slate-300">
          Ouvrir le fichier du profil actif avec <code className="text-emerald-300">hermes config edit</code>. Son emplacement habituel est <code className="text-emerald-300">~/.hermes/config.yaml</code>.
        </p>
        <CodeBlock>{'hermes config edit'}</CodeBlock>
      </Panel>

      <Panel title="Bloc YAML à ajouter" icon={Settings}>
        <CodeBlock label="Copier le YAML">{yamlConfig}</CodeBlock>
        <div className="mt-4 rounded-xl border border-amber-300/25 bg-amber-400/10 p-4 text-sm leading-6 text-amber-100">
          <strong>Important :</strong> Si c’est votre premier serveur MCP, conserver la ligne <code>mcp_servers:</code>. Si une section <code>mcp_servers</code> existe déjà, retirer uniquement la ligne <code>mcp_servers:</code> du bloc à coller, puis ajouter l’entrée <code>{profile.serverName}:</code> sous la section existante en respectant l’indentation. Ne jamais créer deux sections <code>mcp_servers</code>.
        </div>
      </Panel>

      <Panel title="Méthode recommandée — commandes Hermes" icon={Terminal} tone="emerald">
        <p className="text-sm leading-6 text-slate-300">
          Ces commandes ajoutent ou mettent à jour seulement <code className="text-emerald-300">{profile.serverName}</code> sans remplacer les autres serveurs MCP.
        </p>
        <CodeBlock>{commands}</CodeBlock>
      </Panel>

      <Panel title="Contrôle sans afficher la clé" icon={ClipboardCheck}>
        <CodeBlock>{`hermes config get mcp_servers.${profile.serverName}`}</CodeBlock>
        <p className="text-sm leading-6 text-slate-300">
          L’autorisation doit rester une référence à <code className="text-emerald-300">Bearer ${`{${profile.environmentVariable}}`}</code>, jamais une vraie clé copiée dans le YAML.
        </p>
      </Panel>
    </div>
  );
}

function EnvTab({ profile }: { profile: McpConnectionProfile }) {
  return (
    <div className="space-y-5">
      <Panel title="Ajouter la clé dans .env" icon={FileKey}>
        <p className="text-sm leading-6 text-slate-300">Ouvrir le fichier secret local du profil Hermes actif :</p>
        <CodeBlock>{`nano "\${HERMES_HOME:-$HOME/.hermes}/.env"`}</CodeBlock>
        <p className="text-sm leading-6 text-slate-300">Ajouter une seule définition, puis remplacer la valeur indicative localement :</p>
        <CodeBlock label="Copier le modèle">{`${profile.environmentVariable}=COLLER_LA_CLE_ICI`}</CodeBlock>
      </Panel>

      <Panel title="Protéger le fichier" icon={LockKeyhole} tone="emerald">
        <CodeBlock>{`chmod 600 "\${HERMES_HOME:-$HOME/.hermes}/.env"\nstat -c '%A %U:%G %n' "\${HERMES_HOME:-$HOME/.hermes}/.env"`}</CodeBlock>
        <p className="text-sm leading-6 text-slate-300">Le résultat attendu commence par <code className="text-emerald-300">-rw-------</code>.</p>
      </Panel>

      <Panel title="Éviter les doublons" icon={AlertTriangle} tone="amber">
        <p className="text-sm leading-6 text-slate-300">
          Une seule définition active de <code className="text-emerald-300">{profile.environmentVariable}</code> doit exister. Remplacer une ancienne valeur au lieu d’ajouter une deuxième ligne.
        </p>
      </Panel>
    </div>
  );
}

function ValidationTab({ profile }: { profile: McpConnectionProfile }) {
  return (
    <div className="space-y-5">
      <Panel title="Recharger Hermes" icon={RefreshCw}>
        <p className="text-sm leading-6 text-slate-300">Redémarrer le processus Hermes ou sa passerelle pour recharger <code className="text-emerald-300">config.yaml</code> et <code className="text-emerald-300">.env</code>.</p>
      </Panel>

      <Panel title="Tester la connexion MCP" icon={ClipboardCheck} tone="emerald">
        <CodeBlock>{`hermes mcp list\nhermes mcp test ${profile.serverName}`}</CodeBlock>
        <Bullets items={[
          <><code className="text-emerald-300">{profile.serverName}</code> apparaît comme configuré et activé.</>,
          <>Le test rejoint <code className="text-emerald-300">{profile.url}</code>.</>,
          <>La découverte retourne les outils sans erreur d’authentification.</>,
          <>Aucune clé réelle n’est affichée dans une capture ou un journal partagé.</>,
        ]} />
      </Panel>

      <Panel title="Critère de réussite" icon={CheckCircle2}>
        <p className="text-sm leading-6 text-slate-300">
          La configuration est réussie seulement quand <code className="text-emerald-300">hermes mcp test {profile.serverName}</code> confirme la connexion et découvre les outils. La présence du YAML seule ne suffit pas.
        </p>
      </Panel>

      <Panel title="Dépannage rapide" icon={AlertTriangle} tone="amber">
        <Bullets items={[
          <>Erreur <strong className="text-white">401/403</strong> : vérifier que la clé est valide et autorisée, sans jamais l’afficher.</>,
          <>Variable absente : confirmer que <code className="text-emerald-300">{profile.environmentVariable}</code> se trouve dans le <code>.env</code> du profil actif.</>,
          <>Serveur absent : vérifier le nom exact <code className="text-emerald-300">{profile.serverName}</code>, l’indentation YAML et le redémarrage.</>,
          <>Connexion impossible : valider la résolution DNS et l’accès HTTPS au point de terminaison.</>,
        ]} />
      </Panel>
    </div>
  );
}

function SecurityTab({ profile }: { profile: McpConnectionProfile }) {
  return (
    <div className="space-y-5">
      <Panel title="Règles non négociables" icon={ShieldCheck} tone="rose">
        <Bullets items={[
          'La clé MCP ne doit jamais être copiée dans config.yaml, Git, PRIsME, un billet, une capture ou une conversation.',
          'Le fichier .env doit demeurer hors du dépôt et protégé avec des permissions 0600.',
          'Une clé soupçonnée d’exposition doit être révoquée et remplacée immédiatement.',
          `Les permissions de ${profile.serviceLabel} doivent respecter le moindre privilège.`,
          'Les actions sensibles, financières ou irréversibles doivent conserver la validation humaine prévue par la gouvernance BZ.',
        ]} />
      </Panel>
    </div>
  );
}

export default function McpConnectionModal({ isOpen, onClose, profile }: McpConnectionModalProps) {
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const previouslyFocusedElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    setActiveTab('overview');
    const focusFrame = window.requestAnimationFrame(() => dialogRef.current?.focus());

    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      if (event.key === 'Tab' && dialogRef.current) {
        const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>('button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'));
        if (focusable.length === 0) {
          event.preventDefault();
          dialogRef.current.focus();
          return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (document.activeElement === dialogRef.current || !dialogRef.current.contains(document.activeElement)) {
          event.preventDefault();
          (event.shiftKey ? last : first).focus();
        } else if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = previousOverflow;
      previouslyFocusedElement?.focus();
    };
  }, [isOpen, onClose]);

  if (!profile) return null;

  const content = {
    overview: <OverviewTab profile={profile} />,
    config: <ConfigTab profile={profile} />,
    env: <EnvTab profile={profile} />,
    validation: <ValidationTab profile={profile} />,
    security: <SecurityTab profile={profile} />,
  }[activeTab];
  const titleId = `mcp-${profile.serverName}-modal-title`;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/80 p-3 backdrop-blur-sm md:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onMouseDown={onClose}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
        >
          <motion.div
            ref={dialogRef}
            tabIndex={-1}
            className="flex max-h-[94vh] w-full max-w-7xl flex-col overflow-hidden rounded-3xl border border-cyan-300/20 bg-gradient-to-br from-slate-950 via-slate-900 to-cyan-950 shadow-2xl outline-none"
            initial={{ y: 30, scale: 0.98 }}
            animate={{ y: 0, scale: 1 }}
            exit={{ y: 20, scale: 0.98 }}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <header className="flex items-start justify-between gap-4 border-b border-white/10 bg-black/20 px-5 py-5 md:px-7">
              <div className="flex gap-4">
                <div className="rounded-2xl bg-cyan-400/10 p-3"><PlugZap className="h-7 w-7 text-cyan-300" /></div>
                <div>
                  <p className="text-xs font-black uppercase tracking-[0.25em] text-cyan-300">Procédure interne BZ</p>
                  <h2 id={titleId} className="mt-1 text-2xl font-black text-white md:text-3xl">{profile.title}</h2>
                  <p className="mt-1 text-sm text-slate-300">Hermes Agent · configuration sécurisée · validation de connexion</p>
                </div>
              </div>
              <button type="button" onClick={onClose} className="rounded-xl border border-white/10 bg-white/5 p-2 text-slate-300 hover:bg-white/10 hover:text-white" aria-label="Fermer">
                <X className="h-5 w-5" />
              </button>
            </header>

            <nav role="tablist" className="flex gap-2 overflow-x-auto border-b border-white/10 px-4 py-3 md:px-7" aria-label={`Sections de la procédure ${profile.title}`}>
              {tabs.map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  id={`mcp-${profile.serverName}-tab-${id}`}
                  role="tab"
                  aria-selected={activeTab === id}
                  aria-controls={`mcp-${profile.serverName}-panel-${id}`}
                  type="button"
                  onClick={() => setActiveTab(id)}
                  className={`flex shrink-0 items-center gap-2 rounded-xl px-3 py-2 text-xs font-bold transition ${activeTab === id ? 'bg-cyan-400 text-slate-950' : 'bg-white/5 text-slate-300 hover:bg-white/10'}`}
                >
                  <Icon className="h-4 w-4" />{label}
                </button>
              ))}
            </nav>

            <div id={`mcp-${profile.serverName}-panel-${activeTab}`} role="tabpanel" aria-labelledby={`mcp-${profile.serverName}-tab-${activeTab}`} className="overflow-y-auto p-4 md:p-7">
              {content}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
